import _defineProperty from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import { Store } from "pullstate";
import constants from "./constants";

// Terms used throughout this feature:
// - Proxier account: the logged-in user - the real user doing the proxying.
// - Proxied account: the account being acted on behalf of. Also called the "target"
//   account; its pk is what goes in the X-PROXY-TARGET-ACCOUNT header.

export var PROXY_TARGET_HEADER = "X-PROXY-TARGET-ACCOUNT";
var STORAGE_KEY = constants.PERSISTED.proxyTargetAccount;

// What we save to localStorage.

export var readStoredProxyTarget = function readStoredProxyTarget() {
  if (typeof window === "undefined") return null;
  try {
    var raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (!(parsed !== null && parsed !== void 0 && parsed.proxierEmail) || typeof (parsed === null || parsed === void 0 ? void 0 : parsed.proxiedAccountId) !== "number") return null;
    return parsed;
  } catch (e) {
    return null;
  }
};
var writeStoredProxyTarget = function writeStoredProxyTarget(proxierEmail, proxiedAccountId) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      proxierEmail: proxierEmail,
      proxiedAccountId: proxiedAccountId
    }));
  } catch (e) {
    // Storage unavailable: works for this page load, just is not restored later.
  }
};
var removeStoredProxyTarget = function removeStoredProxyTarget() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Storage unavailable: caller still clears the in-memory store, so it's fine.
  }
};

// Read the saved target from localStorage to fill the store on page load, so the
// first request after a refresh already proxies. If it's stale,
// useProxyAccountSession clears it after checking /access.
var loadProxiedAccountIdFromStorage = function loadProxiedAccountIdFromStorage() {
  var _readStoredProxyTarge, _readStoredProxyTarge2;
  return (_readStoredProxyTarge = (_readStoredProxyTarge2 = readStoredProxyTarget()) === null || _readStoredProxyTarge2 === void 0 ? void 0 : _readStoredProxyTarge2.proxiedAccountId) !== null && _readStoredProxyTarge !== void 0 ? _readStoredProxyTarge : null;
};

// Standalone store, not a field on utils/Store.ts: that would close an import
// cycle with axiosConnect.
var ProxyAccountStore = new Store({
  targetAccountId: loadProxiedAccountIdFromStorage(),
  revocationToken: 0
});

// Filters are saved per browser, not per account, so they must not follow the
// user into a different account.
var clearPersistedFilters = function clearPersistedFilters() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(constants.PERSISTED.filterObj);
    window.localStorage.removeItem(constants.PERSISTED.savedFilterData);
    window.localStorage.removeItem(constants.PERSISTED.sortParams);
  } catch (e) {
    // Storage unavailable: nothing was persisted anyway.
  }
};

// Saves the chosen target to both localStorage and the store.
export var setProxyTarget = function setProxyTarget(accountId, userEmail) {
  clearPersistedFilters();
  writeStoredProxyTarget(userEmail, accountId);
  ProxyAccountStore.update(function (store) {
    store.targetAccountId = accountId;
  });
};

// Re-activates an already-saved target that /access has just confirmed is still
// granted. accountId is the source of truth: if localStorage has drifted from it,
// re-sync storage so the store and localStorage can never disagree.
export var confirmProxyTarget = function confirmProxyTarget(accountId) {
  var stored = readStoredProxyTarget();
  if (stored && stored.proxiedAccountId !== accountId) {
    writeStoredProxyTarget(stored.proxierEmail, accountId);
  }
  ProxyAccountStore.update(function (store) {
    store.targetAccountId = accountId;
  });
};
export var clearProxyTarget = function clearProxyTarget() {
  clearPersistedFilters();
  removeStoredProxyTarget();
  ProxyAccountStore.update(function (store) {
    store.targetAccountId = null;
  });
};

// Stops proxying in this tab only, without touching localStorage. For cross-tab
// events: a tab reacting to someone else's change must not delete a key it doesn't own.
export var stopProxyingLocally = function stopProxyingLocally() {
  ProxyAccountStore.update(function (store) {
    store.targetAccountId = null;
  });
};

// Read path for non-React callers (the axios request interceptor).
export var getProxyTargetAccountId = function getProxyTargetAccountId() {
  return ProxyAccountStore.getRawState().targetAccountId;
};

// True while proxying.
export var isProxying = function isProxying() {
  return getProxyTargetAccountId() !== null;
};

// A stale target must never ride a login/CSRF request and get it rejected.
var AUTH_CONTROL_PLANE_URLS = ["/_allauth/", "/set_csrf/", "/auth/", "/login/", "/logout/", "/csrf/"];

// Administering proxy access is never itself proxied.
var PROXY_ADMIN_URL = "/api/account-proxy/";

// The one proxy-header policy, shared by this app's axios client and, via
// registerProxyAuthHeaders, the instance inside @qure/qure-auth.
export var getProxyHeadersForUrl = function getProxyHeadersForUrl(url) {
  var _url$includes;
  var targetAccountId = getProxyTargetAccountId();
  var isAuthControlPlane = AUTH_CONTROL_PLANE_URLS.some(function (path) {
    return url === null || url === void 0 ? void 0 : url.includes(path);
  });
  var isProxyAdmin = (_url$includes = url === null || url === void 0 ? void 0 : url.includes(PROXY_ADMIN_URL)) !== null && _url$includes !== void 0 ? _url$includes : false;
  if (!targetAccountId || isAuthControlPlane || isProxyAdmin) return {};
  return _defineProperty({}, PROXY_TARGET_HEADER, String(targetAccountId));
};

// Called when the proxy grant is revoked (or expires) mid-session and the backend
// explicitly sends the "proxied": false flag on the 403 - not a normal permission 403.
// Clears the target everywhere and bumps revocationToken so ProxyAccountSessionManager
// can toast and refetch the profile back onto the real user.
export var notifyProxyRevoked = function notifyProxyRevoked() {
  clearPersistedFilters();
  removeStoredProxyTarget();
  ProxyAccountStore.update(function (store) {
    store.targetAccountId = null;
    store.revocationToken += 1;
  });
};
export default ProxyAccountStore;